import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, ShoppingCart, Search, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Sheet, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { PortalSheetContent } from '../PortalSheetContent';
import { usePortalDestination, usePortalDraft } from '../PortalDestinationContext';
import { usePortalAuth } from '../PortalAuthContext';
import { PortalRequestDestination } from '../PortalIdentity';
import { QuantityControl } from '../shop/QuantityControl';
import { OTCProductPhoto } from '../shop/OTCProductPhoto';
import { RewardsRedeem } from '../shop/RewardsRedeem';
import { errorText } from '../health/healthConfig';

export default function PortalShopPage() {
  const { api, branch, href, ensureConnection } = usePortalDestination();
  const { patient } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const key = `portal-cart:${patient.patient_user_id}`;
  const [cart, setCart] = useState(() => { try { return JSON.parse(sessionStorage.getItem(key)) || { items: [] }; } catch { return { items: [] }; } });
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  // Contextual shortcut: Account's "My Favourites" row links here with
  // ?favourites=1 to land straight on the filtered view.
  const [favOnly, setFavOnly] = useState(() => new URLSearchParams(location.search).get('favourites') === '1');
  const [page, setPage] = useState(0);
  const [catalog, setCatalog] = useState({ products: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState('collection');
  const [review, setReview] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [loyaltyAccount, setLoyaltyAccount] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const checkoutKey = useRef(sessionStorage.getItem(`${key}:checkout`) || crypto.randomUUID());
  const resetCheckout = () => { checkoutKey.current = crypto.randomUUID(); sessionStorage.setItem(`${key}:checkout`, checkoutKey.current); };
  const branchMismatch = cart.items.length > 0 && cart.tenant_id !== branch?.tenant_id;
  const cartCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);
  usePortalDraft(cart.items.length > 0, busy, cart.items.length
    ? `${cart.items.length} item${cart.items.length > 1 ? 's' : ''} (${cart.items.slice(0, 3).map(i => i.name).join(', ')}${cart.items.length > 3 ? ', …' : ''})`
    : '');
  useEffect(() => { sessionStorage.setItem(key, JSON.stringify(cart)); sessionStorage.setItem(`${key}:checkout`, checkoutKey.current); }, [cart, key]);
  useEffect(() => { if (branchMismatch) setCartOpen(true); }, [branchMismatch]);
  const load = useCallback(async () => {
    if (!branch) return;
    setLoading(true);
    try {
      if (favOnly) {
        const r = await api.get('/otc/favourites');
        setCatalog(prev => ({ ...prev, products: r.data.products, has_more: false }));
      } else {
        const r = await api.get('/otc/products', { params: { search, page, category: category === 'All' ? undefined : category } });
        setCatalog(r.data);
      }
      setError('');
    } catch (e) { setError(errorText(e)); } finally { setLoading(false); }
  }, [api, branch, search, page, category, favOnly]);
  useEffect(() => { const timer = setTimeout(load, 200); return () => clearTimeout(timer); }, [load]);
  const toggleFavourite = async (p) => {
    const next = !p.is_favourite;
    setCatalog(prev => ({ ...prev, products: favOnly && !next
      ? prev.products.filter(x => x.product_id !== p.product_id)
      : prev.products.map(x => x.product_id === p.product_id ? { ...x, is_favourite: next } : x) }));
    try { await (next ? api.put(`/otc/favourites/${p.product_id}`) : api.delete(`/otc/favourites/${p.product_id}`)); }
    catch (e) { toast.error(errorText(e)); load(); }
  };
  useEffect(() => { if (!branchMismatch || !branch) return; let live = true; Promise.all(cart.items.map(i => api.get(`/otc/products/${i.product_id}`).then(r => ({ ...r.data, quantity: i.quantity })).catch(() => ({ ...i, unavailable: true })))).then(rows => { if (live) setReview(rows); }); return () => { live = false; }; }, [api, branch, branchMismatch, cart.items]);
  useEffect(() => { if (!branch) return; let live = true; api.get('/loyalty').then(r => { if (live) setLoyaltyAccount((r.data.accounts || []).find(a => a.tenant_id === branch.tenant_id) || null); }).catch(() => { if (live) setLoyaltyAccount(null); }); return () => { live = false; }; }, [api, branch]);
  const update = (product, quantity) => { if (!branch || branchMismatch) return; resetCheckout(); setRedeemPoints(0); setCart(previous => ({ tenant_id: branch.tenant_id, branch_name: branch.name, currency: catalog.currency, path: href('shop'),
    items: quantity ? [...previous.items.filter(i => i.product_id !== product.product_id), { ...product, quantity }] : previous.items.filter(i => i.product_id !== product.product_id) })); };
  const total = cart.items.reduce((sum, i) => sum + Math.round(i.unit_price * 100) * i.quantity, 0);
  const loyaltyDiscount = redeemPoints > 0 && loyaltyAccount?.redeem_ratio
    ? Math.min(total, Math.round((redeemPoints / loyaltyAccount.redeem_ratio) * 100)) : 0;
  const payable = total - loyaltyDiscount;
  const checkout = async () => {
    setBusy(true); setError(''); try {
      if (!await ensureConnection()) return;
      const r = await api.post('/otc/orders', { tenant_id: branch.tenant_id, payment_choice: choice, idempotency_key: checkoutKey.current, expected_total_minor: payable, loyalty_redeem_points: redeemPoints, items: cart.items.map(i => ({ product_id: i.product_id, quantity: i.quantity })) });
      setCart({ items: [] }); sessionStorage.removeItem(key); setRedeemPoints(0);
      if (r.data.authorization_url && r.data.payment_status === 'unpaid') window.location.assign(r.data.authorization_url);
      else { toast.success(choice === 'collection' ? 'Request sent; awaiting pharmacy confirmation' : 'Order saved'); setCartOpen(false); navigate(href('orders')); }
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };

  const cartHasAnything = cart.items.length > 0 || branchMismatch;

  return (
    <div className="space-y-4 portal-shop-page" data-testid="portal-shop-page">
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-xl font-bold flex gap-2 items-center"><ShoppingBag size={22} />Shop OTC</h1>
        <button type="button" className="portal-shop-cart-trigger" onClick={() => setCartOpen(true)}
          aria-label={`View cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`} data-testid="otc-cart-trigger">
          <ShoppingCart size={20} />
          {cartCount > 0 && <span className="portal-shop-cart-badge" data-testid="otc-cart-count">{cartCount}</span>}
        </button>
      </div>
      <PortalRequestDestination testId="otc-collection-branch" />
      {error && <p role="alert" className="text-red-700" data-testid="otc-shop-error">{error}</p>}

      {branch && <>
        {!favOnly && (
          <div className="relative portal-shop-search">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search OTC products" aria-label="Search OTC products" data-testid="otc-search" />
          </div>
        )}

        <div className="portal-shop-categories" data-testid="otc-categories">
          <button type="button" className={`portal-shop-fav-chip${favOnly ? ' is-active' : ''}`}
            onClick={() => setFavOnly(v => !v)} data-testid="otc-category-favourites">
            <Star size={13} fill={favOnly ? 'currentColor' : 'none'} aria-hidden="true" /> Favourites
          </button>
          {!favOnly && ['All', ...(catalog.categories || [])].map(c => (
            <button type="button" key={c} className={category === c ? 'is-active' : ''}
              onClick={() => { setCategory(c); setPage(0); }} data-testid={`otc-category-${c}`}>
              {c}
            </button>
          ))}
        </div>

        {loading && <p data-testid="otc-loading">Loading products…</p>}
        {!loading && !catalog.products.length && (
          <p className="portal-empty" data-testid="otc-empty">
            {favOnly ? "You haven't starred any products yet. Tap the star on a product to shortlist it for next time." : "No OTC products available in this branch's shop."}
          </p>
        )}

        <div className="otc-grid">{catalog.products.map(p => (
          <article key={p.product_id} className="otc-product" data-testid={`otc-product-${p.product_id}`}>
            <div className="otc-product-image-wrap">
              <OTCProductPhoto product={p} />
              <button type="button" className={`otc-fav-toggle${p.is_favourite ? ' is-active' : ''}`}
                onClick={() => toggleFavourite(p)}
                aria-label={p.is_favourite ? `Remove ${p.name} from favourites` : `Add ${p.name} to favourites`}
                aria-pressed={p.is_favourite} data-testid={`otc-fav-toggle-${p.product_id}`}>
                <Star size={16} fill={p.is_favourite ? 'currentColor' : 'none'} />
              </button>
            </div>
            <div className="otc-product-info">
              <h2 className="font-semibold break-words" data-testid={`otc-product-name-${p.product_id}`}>{p.name}</h2>
              {p.description && p.description !== p.name && (
                <p className="otc-product-variant" data-testid={`otc-product-variant-${p.product_id}`}>{p.description}</p>
              )}
              {p.test_selection && <span className="text-xs text-slate-600" data-testid={`otc-test-selection-${p.product_id}`}>OTC test selection</span>}
              <div className="otc-product-meta">
                <strong data-testid={`otc-price-${p.product_id}`}>{catalog.currency} {Number(p.unit_price).toFixed(2)}</strong>
                <span className="text-xs text-slate-500" data-testid={`otc-stock-${p.product_id}`}>{p.available_quantity ? `${p.available_quantity} available` : 'Out of stock'}</span>
              </div>
            </div>
            <QuantityControl id={`catalog-${p.product_id}`} max={branchMismatch ? 0 : p.available_quantity} value={branchMismatch ? 0 : cart.items.find(i => i.product_id === p.product_id)?.quantity || 0} onChange={q => update(p, q)} />
          </article>
        ))}</div>
        {!favOnly && (page > 0 || catalog.has_more) && <div className="flex gap-2"><Button variant="outline" disabled={!page} onClick={() => setPage(page - 1)} data-testid="otc-prev-page">Previous</Button><Button variant="outline" disabled={!catalog.has_more} onClick={() => setPage(page + 1)} data-testid="otc-next-page">Next</Button></div>}
      </>}

      {cartHasAnything && !cartOpen && (
        <button type="button" className="portal-shop-sticky-cart" onClick={() => setCartOpen(true)} data-testid="otc-sticky-cart-bar">
          <span>{branchMismatch ? 'Review your cart' : `${cartCount} item${cartCount === 1 ? '' : 's'}`}</span>
          {!branchMismatch && <span className="font-semibold">{cart.currency} {(payable / 100).toFixed(2)}</span>}
          <span className="portal-shop-sticky-cart-cta">View cart</span>
        </button>
      )}

      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <PortalSheetContent side="bottom" className="portal-mobile-sheet portal-shop-cart-sheet" data-testid="otc-cart-sheet">
          <div className="portal-sheet-handle" aria-hidden="true" />
          <SheetTitle data-testid="otc-cart-title">Your collection cart</SheetTitle>
          <SheetDescription className="sr-only">Review items, choose payment, and checkout</SheetDescription>

          {branchMismatch && (
            <section className="portal-notice space-y-3" data-testid="otc-branch-review">
              <h2 className="font-semibold">Review your cart for this branch</h2>
              <p>Your original cart belongs to {cart.branch_name}. No products have been substituted.</p>
              {review ? <>
                <ul className="text-sm space-y-1">{review.map(i => <li key={i.product_id} data-testid={`otc-recheck-${i.product_id}`}>{i.name}: {i.unavailable ? 'Not offered here — will be removed' : `${catalog.currency || cart.currency} ${i.unit_price.toFixed(2)} · ${i.available_quantity} available`}</li>)}</ul>
                <Button disabled={!branch} onClick={() => { setCart({ tenant_id: branch.tenant_id, branch_name: branch.name, currency: catalog.currency, path: href('shop'), items: review.filter(i => !i.unavailable) }); setReview(null); setRedeemPoints(0); }} data-testid="otc-accept-rechecked-cart">Confirm checked cart at this branch</Button>
              </> : <p>Checking products, prices and availability…</p>}
              <Button variant="outline" onClick={() => { setCartOpen(false); navigate(cart.path); }} data-testid="otc-keep-original-cart">Return to original cart</Button>
            </section>
          )}

          {!branchMismatch && cart.items.length === 0 && (
            <p className="portal-empty" data-testid="otc-cart-empty">Your cart is empty. Add OTC products to get started.</p>
          )}

          {!branchMismatch && !!cart.items.length && (
            <section className="space-y-4" data-testid="otc-cart">
              {cart.items.map(i => (
                <div className="flex flex-wrap justify-between gap-3 items-center border-b pb-3" key={i.product_id}>
                  <div className="min-w-0 flex-1 break-words" data-testid={`otc-cart-item-${i.product_id}`}>
                    {i.name}
                    <p className="text-sm text-slate-500">{cart.currency} {Number(i.unit_price).toFixed(2)} each</p>
                    {i.quantity > i.available_quantity && <p className="text-red-700 text-sm">Reduce quantity to available stock.</p>}
                  </div>
                  <QuantityControl id={`cart-${i.product_id}`} max={i.available_quantity} value={i.quantity} onChange={q => update(i, q)} />
                </div>
              ))}
              <div className="flex justify-between font-bold text-lg" data-testid="otc-cart-total"><span>Total</span><span>{cart.currency} {(total / 100).toFixed(2)}</span></div>
              <RewardsRedeem account={loyaltyAccount} points={redeemPoints} onChange={setRedeemPoints} subtotalMinor={total} currency={cart.currency} />
              {loyaltyDiscount > 0 && (
                <div className="flex justify-between font-bold text-lg text-emerald-700" data-testid="otc-cart-payable"><span>You'll pay</span><span>{cart.currency} {(payable / 100).toFixed(2)}</span></div>
              )}
              <fieldset className="space-y-3">
                <legend className="font-medium mb-2">Payment choice</legend>
                <label className="portal-check"><input type="radio" checked={choice === 'collection'} onChange={() => { setChoice('collection'); checkoutKey.current = crypto.randomUUID(); }} data-testid="otc-pay-collection" />Request order / pay at collection</label>
                <label className="portal-check"><input type="radio" checked={choice === 'online'} disabled={!catalog.online_available} onChange={() => { setChoice('online'); checkoutKey.current = crypto.randomUUID(); }} data-testid="otc-pay-online" />Pay online / collect at pharmacy</label>
              </fieldset>
              {!catalog.online_available && <p className="portal-notice" data-testid="otc-online-unavailable">Online payment is not configured for this pharmacy. You can request an order and pay at collection.</p>}
              {catalog.payment_mode === 'test' || catalog.mode === 'test' ? <p className="portal-notice" data-testid="otc-test-payment">Online payment is in test mode; no real payment is collected.</p> : null}
              <p className="text-sm text-slate-600" data-testid="otc-checkout-explanation">{choice === 'collection' ? 'The pharmacy must confirm availability and accept your request before collection is promised.' : 'Stock is held for 30 minutes. Payment is confirmed only after server verification.'}</p>
              <Button className="portal-primary w-full" disabled={busy || !branch || cart.items.some(i => i.quantity > i.available_quantity) || (choice === 'online' && !catalog.online_available)} onClick={checkout} data-testid="otc-checkout-submit">{busy ? 'Submitting…' : choice === 'collection' ? 'Send collection request' : 'Continue to secure payment'}</Button>
            </section>
          )}
        </PortalSheetContent>
      </Sheet>
    </div>
  );
}
